<template>
  <div class="geetest-wrapper">
    <div :id="containerId" class="geetest-container" />
    <div v-if="isReady && !isVerified" class="geetest-hint">
      {{ $t('auth.captchaClickToVerify') }}
    </div>
    <p v-if="error" class="geetest-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  loadGeetest,
  initGeetestWidget,
  serializeGeetestResult,
  type GeetestCaptchaObject,
  type GeetestValidateResult,
} from '@/shared/utils/geetest'

const props = defineProps<{
  containerId?: string
  autoShow?: boolean
}>()

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'verified', token: string): void
  (e: 'expired'): void
  (e: 'error'): void
  (e: 'cancelled'): void
}>()

const containerId = props.containerId || `geetest-${Math.random().toString(36).slice(2, 9)}`
const captchaObj = ref<GeetestCaptchaObject | null>(null)
const error = ref('')
const token = ref('')
const isReady = ref(false)
const isVerified = ref(false)
let pendingShow = false
let disposed = false

const showWhenReady = () => {
  if (disposed || !pendingShow || !isReady.value || !captchaObj.value) return
  pendingShow = false
  captchaObj.value.showCaptcha()
}

// Retry logic for network errors
const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000
const retryCount = ref(0)

const retryGeetest = async () => {
  if (!captchaObj.value) return
  
  console.log(`[GonkaGame] Retrying GeeTest (attempt ${retryCount.value}/${MAX_RETRIES})`)
  
  await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
  captchaObj.value.reset()
  captchaObj.value.showCaptcha()
}

const handleError = (captchaError?: any) => {
  // Check if this is a network error we can retry
  const isNetworkError = (
    captchaError?.message?.includes('网络错误') ||
    captchaError?.msg?.includes('网络错误') ||
    String(captchaError).includes('网络错误')
  )
  
  if (isNetworkError && retryCount.value < MAX_RETRIES) {
    retryCount.value++
    retryGeetest()
    return
  }
  
  retryCount.value = 0
  pendingShow = false
  error.value = t('auth.captchaGeetestError')
  emit('error')
}

const initialize = async () => {
  isReady.value = false
  try {
    error.value = ''
    await loadGeetest()
    if (disposed) return
    
    const widget = await initGeetestWidget(containerId, {
      onReady: () => {
        isReady.value = true
        if (props.autoShow) pendingShow = true
        showWhenReady()
      },
      onSuccess: (result: GeetestValidateResult) => {
        isVerified.value = true
        retryCount.value = 0 // Reset retries on success
        // Serialize the full result for backend validation
        token.value = serializeGeetestResult(result)
        emit('verified', token.value)
      },
      onError: handleError,
      onClose: () => {
        if (!isVerified.value) {
          pendingShow = false
          emit('cancelled')
        }
      },
    })
    if (disposed) {
      widget.destroy()
      return
    }
    captchaObj.value = widget
    showWhenReady()
  } catch (err) {
    if (disposed) return
    pendingShow = false
    error.value = t('auth.captchaLoadError')
    emit('error')
    console.error('[GonkaGame] Geetest load error:', err)
  }
}

onMounted(initialize)

onBeforeUnmount(() => {
  disposed = true
  pendingShow = false
  if (captchaObj.value) {
    try {
      captchaObj.value.destroy()
    } catch {
      // Ignore destroy errors
    }
  }
})

/**
 * Reset the widget for re-verification
 */
const reset = () => {
  if (captchaObj.value) {
    captchaObj.value.reset()
    token.value = ''
    isVerified.value = false
  }
}

/**
 * Get current token (for external use)
 */
const getToken = (): string => {
  return token.value
}

/**
 * Trigger captcha popup (for bind mode)
 */
const showCaptcha = () => {
  if (disposed) return
  if (error.value) {
    error.value = ''
    retryCount.value = 0
    isVerified.value = false
    if (captchaObj.value) {
      captchaObj.value.reset()
    } else {
      void initialize()
    }
  }
  pendingShow = true
  showWhenReady()
}

// Expose methods for parent components
defineExpose({
  reset,
  getToken,
  showCaptcha,
})
</script>

<style scoped>
.geetest-wrapper {
  margin: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.geetest-container {
  display: flex;
  justify-content: center;
  min-height: 44px;
  width: 100%;
}

/* Center the Geetest widget itself */
.geetest-container :deep(.geetest_btn),
.geetest-container :deep(.geetest_holder) {
  margin: 0 auto;
}

.geetest-hint {
  text-align: center;
  font-size: var(--p-text-13);
  color: var(--hm-text-secondary);
  margin-top: 8px;
}

.geetest-error {
  color: var(--hm-danger);
  font-size: var(--ds-text-size-body);
  margin-top: 8px;
  text-align: center;
}
</style>
